import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Send, Users, MessageCircle, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CircleAvatar } from "@/components/CircleAvatar";
import { BlessPicker } from "@/components/BlessPicker";
import { api } from "@/lib/api";
import { userCoords } from "@/hooks/useGeo";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { normFeed, tierColor, money, PRESET_AMOUNTS, UNLIMITED_RADIUS, clockTime } from "@/lib/blessed";
import LiveChatBanner from "@/components/LiveChatBanner";
import { celebrate } from "@/lib/effects";

const ChatRow = ({ item, onHeart, onThanks, onRow, onEdit }) => {
  const color = tierColor(item.amount);
  const isChatOnly = (Number(item.amount) || 0) === 0 && item.recipientId === "chat";
  return (
    <div className="py-2" data-testid={`chat-row-${item.id}`}>
      <div className="flex items-start gap-3">
        <button onClick={() => onRow(item)} className="shrink-0">
          <CircleAvatar src={item.donorPicture} name={item.donorName} size={40} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-x-2 gap-y-1 flex-wrap leading-tight">
            <span className="text-xs font-bold text-ink-tertiary tabular-nums">{clockTime(item.createdAt)}</span>
            <button onClick={() => onRow(item)} className="text-sm font-black text-brand-primary hover:underline">{item.donorName}</button>
            {!isChatOnly && (
              <>
                <span className="font-black text-white text-xs px-2 py-0.5 rounded-pill" style={{ background: color }}>{money(item.amount)}</span>
                <span className="text-ink-tertiary font-black text-sm">→</span>
                <span className="text-sm font-black text-ink">{item.recipientName}</span>
                {item.method && <span className="text-[10px] font-black px-1.5 py-0.5 rounded-pill bg-surface-3 text-ink-tertiary uppercase">{item.method}</span>}
              </>
            )}
          </div>
          {item.message && (
            item.isMineAsDonor ? (
              <button data-testid={`chat-message-${item.id}`} onClick={() => onEdit(item)}
                className="text-sm text-ink mt-1 whitespace-pre-wrap break-words text-left hover:opacity-70 transition inline-flex items-start gap-1.5 group">
                <span>{item.message}</span>
                <Pencil size={13} className="mt-0.5 shrink-0 text-ink-tertiary group-hover:text-brand-primary" />
              </button>
            ) : (
              <p data-testid={`chat-message-${item.id}`} className="text-sm text-ink mt-1 whitespace-pre-wrap break-words">{item.message}</p>
            )
          )}
          {item.thanks && (
            item.isMineAsRecipient ? (
              <button data-testid={`thanks-bubble-${item.id}`} onClick={() => onThanks(item)}
                className="mt-1.5 inline-flex items-center gap-1.5 bg-surface border border-border rounded-pill px-3 py-1.5 text-sm text-left hover:brightness-95 transition group">
                <span>🙏 <span className="font-black text-brand-primary">{item.recipientName}:</span> <span className="text-ink">{item.thanks}</span></span>
                <Pencil size={13} className="shrink-0 text-ink-tertiary group-hover:text-brand-primary" />
              </button>
            ) : (
              <div className="mt-1.5 inline-block bg-surface border border-border rounded-pill px-3 py-1.5 text-sm">
                🙏 <span className="font-black text-brand-primary">{item.recipientName}:</span> <span className="text-ink">{item.thanks}</span>
              </div>
            )
          )}
          <div className="flex items-center gap-5 mt-1.5">
            <button data-testid={`heart-${item.id}`} onClick={() => onHeart(item)}
              className={`flex items-center gap-1.5 text-sm font-black ${item.hearted ? "text-errorb" : "text-ink-tertiary hover:text-errorb"} transition-colors`}>
              <Heart size={16} fill={item.hearted ? "currentColor" : "none"} /> {item.hearted ? "Hearted" : "Heart"}
            </button>
            {item.isMineAsDonor && item.message && (
              <button data-testid={`edit-comment-${item.id}`} onClick={() => onEdit(item)} className="flex items-center gap-1.5 text-sm font-black text-ink-tertiary hover:text-brand-primary transition">
                <Pencil size={15} /> Edit comment
              </button>
            )}
            {item.isMineAsRecipient && (
              <button data-testid={`thanks-${item.id}`} onClick={() => onThanks(item)} className="flex items-center gap-1.5 text-sm font-black text-brand-primary hover:brightness-90 transition">
                <MessageCircle size={16} /> {item.thanks ? "Edit thanks" : "Say thanks"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function LivePage() {
  const { user, refreshUser } = useAuth();
  const coords = useMemo(() => userCoords(user), [user]);
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [feed, setFeed] = useState([]);
  const [carDriveKey, setCarDriveKey] = useState(0);
  const [carCaption, setCarCaption] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickAmount, setPickAmount] = useState(null);
  const [pickMessage, setPickMessage] = useState("");
  const [thanksFor, setThanksFor] = useState(null);
  const [thanksText, setThanksText] = useState("");
  const [say, setSay] = useState("");
  const [sending, setSending] = useState(false);
  const [superChatOpen, setSuperChatOpen] = useState(false);
  const [editFor, setEditFor] = useState(null);
  const [editText, setEditText] = useState("");
  const first = useRef(true);
  const lastSeenId = useRef(null);

  const fetchFeed = () => {
    const params = { limit: 60, radius_miles: UNLIMITED_RADIUS };
    if (coords) { params.lat = coords.lat; params.lng = coords.lng; }
    api.get("/feed", { params }).then(({ data }) => {
      const myUserId = user?.user_id || user?.id || user?.sub || user?._id || null;
      const myRecipientId = user?.recipient_id || null;
      const list = (Array.isArray(data) ? data : data.items || data.feed || []).map(normFeed).map((f) => ({
        ...f,
        isMineAsDonor: f.isMineAsDonor || (!!myUserId && f.donorId === myUserId),
        isMineAsRecipient: f.isMineAsRecipient || (!!myRecipientId && f.recipientId === myRecipientId),
      }));
      // Car + toast fire ONLY for a genuinely new PAID donation at the top of the feed.
      // Three guards (matching mobile): (1) real donation — amount>0 and recipientId!=="chat",
      // (2) not the first load, (3) not a stale row promoted by a deletion (newer id but
      // older timestamp). Chat-only comments must never trigger the car.
      const newestId = list[0]?.id || null;
      if (first.current) {
        first.current = false;
        lastSeenId.current = newestId;
      } else if (newestId && newestId !== lastSeenId.current) {
        const fresh = list[0];
        const isRealDonation = Number(fresh?.amount) > 0 && fresh?.recipientId !== "chat";
        const olderThanLastSeen =
          !!lastSeenId.current &&
          !!fresh?.createdAt &&
          (() => {
            const prev = list.find((x) => x.id === lastSeenId.current);
            if (!prev?.createdAt) return false;
            return new Date(fresh.createdAt).getTime() < new Date(prev.createdAt).getTime();
          })();
        lastSeenId.current = newestId;
        if (isRealDonation && !olderThanLastSeen) {
          celebrate();
          setCarDriveKey((k) => k + 1);
          setCarCaption(`${fresh.donorName} → ${fresh.recipientName} ${money(fresh.amount)}`);
          if (fresh.isMineAsRecipient) toast.success(`🎉 You received ${money(fresh.amount)} from ${fresh.donorName}! Arrives in your bank in ~2 days.`);
          else toast(`💸 ${fresh.donorName} blessed ${fresh.recipientName} ${money(fresh.amount)}`);
        }
      }
      setFeed(list);
    }).catch(() => {});
  };

  useEffect(() => { fetchFeed(); const t = setInterval(fetchFeed, 8000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [coords]);

  const heart = async (item) => {
    setFeed((f) => f.map((x) => (x.id === item.id ? { ...x, hearted: !x.hearted } : x)));
    try { await api.post(`/donations/${item.id}/heart`); }
    catch (e) { toast.error("Only the recipient can heart this gift."); fetchFeed(); }
  };

  const sendThanks = async () => {
    if (!thanksText.trim()) return;
    try {
      await api.post(`/donations/${thanksFor.id}/thanks`, { text: thanksText.trim() });
      toast.success("Thank-you sent 💜");
      setThanksFor(null); setThanksText(""); fetchFeed();
    } catch (e) { toast.error("Could not send thanks."); }
  };

  const openPicker = (amount) => { setPickAmount(amount); setPickMessage(""); setPickerOpen(true); };
  const donateTarget = (item) => {
    // Chat-only comments use recipient_id "chat" (no real recipient) → bless the commenter instead.
    if (item.recipientId === "chat" || (user?.recipient_id && user.recipient_id === item.recipientId)) {
      if (item.anonymous || !item.donorRecipientId) return null;
      return item.donorRecipientId;
    }
    return item.recipientId;
  };
  const rowClick = (item) => { const t = donateTarget(item); if (t) navigate(`/donate/${t}`); };

  const openEdit = (item) => { setEditFor(item); setEditText(item.message || ""); };
  const openThanks = (item) => { setThanksFor(item); setThanksText(item.thanks || ""); };

  const sendFree = async () => {
    const text = say.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/chat/messages", { text });
      setSay("");
      const row = { ...normFeed(data), isMineAsDonor: true };
      if (row.id) lastSeenId.current = row.id;
      setFeed((f) => [row, ...f.filter((x) => x.id !== row.id)]);
    } catch (e) {
      toast.error("Couldn't post comment.");
    } finally {
      setSending(false);
    }
  };

  const saveComment = async () => {
    const text = editText.trim();
    if (!text) return;
    try {
      await api.patch(`/donations/${editFor.id}/message`, { text });
      toast.success("Comment updated");
      setEditFor(null); fetchFeed();
    } catch (e) { toast.error("Could not update comment."); }
  };

  const deleteComment = async () => {
    try {
      await api.delete(`/donations/${editFor.id}/message`);
      toast.success("Comment deleted — your blessing stays 💜");
      setEditFor(null); fetchFeed();
    } catch (e) { toast.error("Could not delete comment."); }
  };

  const lastHour = feed.filter((f) => f.createdAt && Date.now() - new Date(f.createdAt).getTime() < 3600000).length;

  const bannerBase = `${process.env.REACT_APP_BACKEND_URL}/api/proxy`;
  const customBannerSrc = user?.feed_banner_url
    ? (user.feed_banner_url.startsWith("http") ? user.feed_banner_url : `${bannerBase}${user.feed_banner_url.replace(/^\/api/, "")}`)
    : null;
  const uploadBanner = async (file) => {
    const form = new FormData();
    form.append("file", file);
    try { await api.post("/users/me/feed-banner", form); await refreshUser(); toast.success("Banner updated 🎉"); }
    catch (e) { toast.error("Could not upload banner."); }
  };
  const deleteBanner = async () => {
    try { await api.delete("/users/me/feed-banner"); await refreshUser(); toast.success("Banner removed"); }
    catch (e) { toast.error("Could not remove banner."); }
  };

  return (
    <div className="flex flex-col h-[100dvh] overflow-hidden">
      {/* Header */}
      <div className="border-b border-border bg-surface-2 px-4 pt-4 pb-3">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-black">Live chat</h1>
            <p className="text-sm font-bold text-ink-tertiary flex items-center gap-1 mt-0.5"><Users size={15} /> Top chat · {lastHour} in the last hour</p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-sm font-black">Dark Mode</span>
            <Switch data-testid="dark-mode-toggle" checked={theme === "dark"} onCheckedChange={toggle} />
            <span className="w-2 h-2 rounded-pill bg-errorb animate-pulse" />
          </label>
        </div>
        <div className="flex gap-2 overflow-x-auto slim-scroll mt-3">
          {PRESET_AMOUNTS.map((a) => (
            <button key={a} data-testid={`quick-amount-${a}`} onClick={() => openPicker(a)}
              className="shrink-0 px-5 py-1.5 rounded-pill font-black text-white transition-transform hover:scale-105" style={{ background: tierColor(a) }}>
              {money(a)}
            </button>
          ))}
          <button data-testid="quick-amount-custom" onClick={() => openPicker(null)}
            className="shrink-0 px-5 py-1.5 rounded-pill font-black text-ink bg-surface-3 border border-border transition-transform hover:scale-105">
            Custom
          </button>
          <button data-testid="test-car-button" title="Test car drive-by" onClick={() => { setCarCaption("Maria → James $20"); setCarDriveKey((k) => k + 1); }}
            className="shrink-0 px-4 py-1.5 rounded-pill font-black text-ink bg-surface-3 border border-border transition-transform hover:scale-105 text-lg">
            🚗
          </button>
        </div>
        <p className="text-sm text-ink-tertiary font-semibold mt-2">Tap an amount to pick who to bless · tap any comment to bless them directly</p>
      </div>

      {/* Pinned banner (tap to upload · car drive-by on new blessings) */}
      <LiveChatBanner
        defaultBannerSrc="/assets/feed-banner-default.png"
        customBannerSrc={customBannerSrc}
        carSrcs={{ johnwick: "/assets/car-johnwick.png", tate: "/assets/car-tate.png" }}
        carDriveKey={carDriveKey}
        caption={carCaption}
        onPickBanner={uploadBanner}
        onRemoveBanner={deleteBanner}
      />

      {/* Feed */}
      <div className="flex-1 overflow-y-auto slim-scroll px-4 pb-[calc(env(safe-area-inset-bottom)+150px)] lg:pb-[88px]">
        {feed.length === 0 && <div className="text-center text-ink-tertiary py-20 font-bold">No blessings yet. Tap an amount to start.</div>}
        {feed.map((item) => (
          <ChatRow key={item.id} item={item} onHeart={heart} onThanks={openThanks} onRow={rowClick} onEdit={openEdit} />
        ))}
      </div>

      {/* Say something — pinned above the nav, never hidden */}
      <div className="fixed z-30 left-0 right-0 bottom-[calc(env(safe-area-inset-bottom)+62px)] lg:left-64 lg:right-0 lg:bottom-0 border-t border-border bg-surface-2 px-3 py-3 flex items-center gap-2" style={{ paddingBottom: "12px" }}>
        <button data-testid="say-something-input" onClick={() => setSuperChatOpen(true)}
          className="flex-1 text-left rounded-pill bg-surface border border-border h-12 px-4 text-ink-tertiary font-semibold">
          Send a Super Chat…
        </button>
        <Button data-testid="super-chat-button" title="Send a Super Chat" onClick={() => setSuperChatOpen(true)}
          className="rounded-pill h-12 w-12 p-0 text-white shrink-0 text-xl font-black" style={{ background: "#22C55E" }}>$</Button>
      </div>

      <BlessPicker open={pickerOpen} onOpenChange={setPickerOpen} amount={pickAmount} message={pickMessage} coords={coords} />

      {/* Super Chat explainer */}
      <Dialog open={superChatOpen} onOpenChange={setSuperChatOpen}>
        <DialogContent className="bg-surface-2 border-border max-w-sm rounded-2xl" data-testid="super-chat-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-left">Send a Super Chat</DialogTitle>
          </DialogHeader>
          <p className="text-base text-ink-tertiary font-semibold leading-relaxed text-left">
            Tap any comment or profile in the chat above to send that person a Super Chat with a message. The bigger the blessing, the brighter your chat pins to the top.
          </p>
          <div className="flex flex-wrap gap-2 my-1">
            {[5, 10, 20, 50, 100, 1000].map((a) => (
              <span key={a} className="px-4 py-2 rounded-pill font-black text-white text-sm" style={{ background: tierColor(a) }}>
                ${a}+
              </span>
            ))}
          </div>
          <Button data-testid="super-chat-got-it" onClick={() => setSuperChatOpen(false)}
            className="w-full h-14 rounded-pill font-black text-lg bg-brand-primary hover:opacity-90 text-white">
            Got it
          </Button>
        </DialogContent>
      </Dialog>

      {/* Edit / delete my comment — deleting keeps the donation */}
      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent className="bg-surface-2 border-border max-w-sm rounded-lg" data-testid="edit-comment-dialog">
          <DialogHeader><DialogTitle className="font-black">Edit your comment</DialogTitle></DialogHeader>
          <Textarea data-testid="edit-comment-textarea" value={editText} onChange={(e) => setEditText(e.target.value)} placeholder="Your message…" className="bg-surface border-border" />
          <p className="text-xs text-ink-tertiary font-semibold">Deleting your comment won't refund or remove your blessing — the gift stays.</p>
          <DialogFooter className="flex-row justify-between gap-2 sm:justify-between">
            <Button data-testid="delete-comment-button" onClick={deleteComment} variant="ghost" className="rounded-pill font-extrabold text-errorb hover:bg-errorb/10 hover:text-errorb">
              <Trash2 size={16} className="mr-1" /> Delete
            </Button>
            <Button data-testid="save-comment-button" onClick={saveComment} className="rounded-pill font-extrabold bg-brand-primary text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!thanksFor} onOpenChange={(o) => !o && setThanksFor(null)}>
        <DialogContent className="bg-surface-2 border-border max-w-sm rounded-lg">
          <DialogHeader><DialogTitle className="font-black">Say thanks</DialogTitle></DialogHeader>
          <Textarea data-testid="thanks-textarea" value={thanksText} onChange={(e) => setThanksText(e.target.value)} placeholder="Your gratitude means a lot…" className="bg-surface border-border" />
          <DialogFooter>
            <Button data-testid="thanks-send" onClick={sendThanks} className="rounded-pill font-extrabold bg-brand-primary text-white">Send thanks</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
